/**
 * Payment Gateway Service
 * Manages payment gateway configurations and integrations
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Available payment gateways
 */
export const PAYMENT_GATEWAYS = {
  STRIPE: 'stripe',
  WISE: 'wise',
  PAYMONGO: 'paymongo',
  GCASH: 'gcash',
  MANUAL: 'manual',
};

/**
 * Create or update payment gateway configuration
 */
export const savePaymentGatewayConfig = async (userId, gatewayData) => {
  try {
    // Check if config already exists
    const q = query(
      collection(db, 'paymentGateways'),
      where('userId', '==', userId),
      where('gateway', '==', gatewayData.gateway)
    );
    
    const snapshot = await getDocs(q);
    
    const configData = {
      userId,
      gateway: gatewayData.gateway,
      isActive: gatewayData.isActive !== false,
      config: gatewayData.config || {},
      displayName: gatewayData.displayName || gatewayData.gateway,
      updatedAt: serverTimestamp(),
    };

    if (!snapshot.empty) {
      // Update existing
      const docRef = doc(db, 'paymentGateways', snapshot.docs[0].id);
      await updateDoc(docRef, configData);
      return { configId: snapshot.docs[0].id, ...configData };
    } else {
      // Create new
      configData.createdAt = serverTimestamp();
      const docRef = await addDoc(collection(db, 'paymentGateways'), configData);
      return { configId: docRef.id, ...configData };
    }
  } catch (error) {
    console.error('Error saving payment gateway config:', error);
    throw error;
  }
};

/**
 * Get payment gateway configuration
 */
export const getPaymentGatewayConfig = async (userId, gateway) => {
  try {
    const q = query(
      collection(db, 'paymentGateways'),
      where('userId', '==', userId),
      where('gateway', '==', gateway)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error getting payment gateway config:', error);
    throw error;
  }
};

/**
 * Get all payment gateway configurations for user
 */
export const getPaymentGatewayConfigs = async (userId) => {
  try {
    const q = query(
      collection(db, 'paymentGateways'),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting payment gateway configs:', error);
    throw error;
  }
};

/**
 * Delete payment gateway configuration
 */
export const deletePaymentGatewayConfig = async (configId) => {
  try {
    const configRef = doc(db, 'paymentGateways', configId);
    await deleteDoc(configRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting payment gateway config:', error);
    throw error;
  }
};

/**
 * Subscribe to payment gateway configs
 */
export const subscribeToPaymentGatewayConfigs = (userId, callback) => {
  const q = query(
    collection(db, 'paymentGateways'),
    where('userId', '==', userId)
  );

  return onSnapshot(q, (snapshot) => {
    const configs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(configs);
  });
};

/**
 * Payment gateway configuration templates
 */
export const GATEWAY_CONFIG_TEMPLATES = {
  stripe: {
    displayName: 'Stripe',
    fields: [
      { name: 'publishableKey', label: 'Publishable Key', type: 'text', required: true },
      { name: 'secretKey', label: 'Secret Key', type: 'password', required: true },
      { name: 'webhookSecret', label: 'Webhook Secret', type: 'password', required: false },
    ],
    instructions: 'Get your API keys from https://dashboard.stripe.com/apikeys',
  },
  wise: {
    displayName: 'Wise (TransferWise)',
    fields: [
      { name: 'apiToken', label: 'API Token', type: 'password', required: true },
      { name: 'profileId', label: 'Profile ID', type: 'text', required: true },
    ],
    instructions: 'Get your API token from Wise Business settings',
  },
  paymongo: {
    displayName: 'PayMongo',
    fields: [
      { name: 'publicKey', label: 'Public Key', type: 'text', required: true },
      { name: 'secretKey', label: 'Secret Key', type: 'password', required: true },
    ],
    instructions: 'Get your API keys from https://dashboard.paymongo.com/developers/api-keys',
  },
  gcash: {
    displayName: 'GCash',
    fields: [
      { name: 'qrCodeImage', label: 'QR Code Image URL', type: 'text', required: true },
      { name: 'accountName', label: 'Account Name', type: 'text', required: true },
      { name: 'accountNumber', label: 'Mobile Number', type: 'text', required: true },
      { name: 'instructions', label: 'Payment Instructions', type: 'textarea', required: false },
    ],
    instructions: 'Upload your GCash QR code and provide account details',
  },
  manual: {
    displayName: 'Manual Payment',
    fields: [
      { name: 'bankName', label: 'Bank Name', type: 'text', required: false },
      { name: 'accountName', label: 'Account Name', type: 'text', required: false },
      { name: 'accountNumber', label: 'Account Number', type: 'text', required: false },
      { name: 'swiftCode', label: 'SWIFT/BIC Code', type: 'text', required: false },
      { name: 'instructions', label: 'Payment Instructions', type: 'textarea', required: false },
    ],
    instructions: 'Provide bank details or payment instructions for clients',
  },
};
